/**
 * Reusable toast notification hook.
 *
 * Provides a simple API to show success/error/info messages
 * via MUI Snackbar instead of inline Alert components.
 */

import { useCallback, useState } from 'react';

export type ToastSeverity = 'success' | 'error' | 'info' | 'warning';

export interface ToastState {
  open: boolean;
  message: string;
  severity: ToastSeverity;
}

export interface UseToastResult {
  toast: ToastState;
  showToast: (message: string, severity?: ToastSeverity) => void;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
  closeToast: () => void;
}

export function useToast(): UseToastResult {
  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'info',
  });

  const showToast = useCallback(
    (message: string, severity: ToastSeverity = 'info'): void => {
      setToast({ open: true, message, severity });
    },
    [],
  );

  const showError = useCallback(
    (message: string): void => {
      setToast({ open: true, message, severity: 'error' });
    },
    [],
  );

  const showSuccess = useCallback(
    (message: string): void => {
      setToast({ open: true, message, severity: 'success' });
    },
    [],
  );

  const closeToast = useCallback((): void => {
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  return { toast, showToast, showError, showSuccess, closeToast };
}
